import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, ArrowUpRight, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { usePartnerAuth } from '../../context/PartnerAuthContext';
import { apiErrorMessage } from '../../lib/apiError';

export default function PartnerNewLead() {
  const nav = useNavigate();
  const { partner, authHeader, API } = usePartnerAuth();
  const [form, setForm] = useState({
    name: '', email: '', phone: '', interest: 'other', message: '',
    deal_value: '', deal_currency: 'GBP',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [busy, setBusy] = useState(false);

  const isApproved = partner.status === 'approved';

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error('Client name and email are required');
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.phone) delete payload.phone;
      if (!payload.message) delete payload.message;
      if (payload.deal_value === '' || payload.deal_value === null) {
        delete payload.deal_value;
      } else {
        payload.deal_value = Number(payload.deal_value);
      }
      await axios.post(`${API}/partner/leads`, payload, { headers: authHeader() });
      toast.success('Referral submitted. Your RM will be in touch.');
      nav('/partner', { replace: true });
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not submit referral'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[720px] mx-auto">
      <Link to="/partner" className="inline-flex items-center gap-1.5 text-[13px] text-[#0B3B2E] hover:text-[#12503F]">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>

      <p className="mt-6 text-[11px] uppercase tracking-[0.25em] text-[#0B3B2E]/70 font-medium">New referral</p>
      <h1 className="mt-2 font-serif text-[32px] lg:text-[40px] leading-tight text-[#0B3B2E]">Submit a client</h1>
      <p className="mt-1 text-[13px] text-[#0E1F1A]/60">
        Give us just enough to reach out. Your assigned RM will take it from there.
      </p>

      {!isApproved && (
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-xl p-4 text-[13px] text-[#0E1F1A]/75">
          Your account is not yet approved. You can start filling in the form, but submissions are only accepted once an admin approves your profile.
        </div>
      )}

      <form onSubmit={submit} className="mt-6 bg-white rounded-2xl border border-[#0B3B2E]/10 p-6 lg:p-8 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <Row label="Client name"><Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Alex Morgan" /></Row>
          <Row label="Client email"><Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="alex@company.com" /></Row>
          <Row label="Phone (optional)"><Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+44 20 …" /></Row>
          <Row label="Interest">
            <select value={form.interest} onChange={(e) => set('interest', e.target.value)}
              className="h-10 w-full rounded-md border border-[#0B3B2E]/20 bg-white px-3 text-[14px]">
              <option value="property">Property</option>
              <option value="business">Business</option>
              <option value="personal">Personal</option>
              <option value="other">Other</option>
            </select>
          </Row>
        </div>
        <Row label="Expected deal value (optional)">
          <div className="flex gap-2">
            <select value={form.deal_currency} onChange={(e) => set('deal_currency', e.target.value)}
              className="h-10 rounded-md border border-[#0B3B2E]/20 bg-white px-3 text-[14px]">
              {['GBP','EUR','USD','AUD','CAD','JPY','AED','ZAR'].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <Input type="number" min="0" value={form.deal_value}
              onChange={(e) => set('deal_value', e.target.value)}
              placeholder="e.g. 850000" className="flex-1" />
          </div>
        </Row>
        <Row label="Notes for the RM">
          <textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={4}
            placeholder="E.g. Ana is buying a villa in Marbella, needs 850k EUR by end of Q3."
            className="w-full rounded-md border border-[#0B3B2E]/20 bg-white p-3 text-[14px]" />
        </Row>

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={() => nav('/partner')}
            className="rounded-full border-[#0B3B2E]/25 bg-transparent text-[#0B3B2E]">Cancel</Button>
          <Button type="submit" disabled={busy || !isApproved}
            className="rounded-full bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F] disabled:opacity-70">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending</>
              : <>Submit referral <ArrowUpRight className="w-4 h-4 ml-1" /></>}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-[12px] text-[#0E1F1A]/65">{label}</Label>
      {children}
    </div>
  );
}
